// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import constants from "@store/constants";
import { blogFeaturedImageField, blogFeaturedTextField } from "@store/Framework/Common/Fields/Toolkit/blog";
import { useBlogCategoryFeaturedArticleDataView } from "@store/Framework/Store/Data/BlogCategory/BlogCategoryHooks";
import loadBlogCategoryFeaturedArticle from "@store/Framework/Store/Starter/Data/Blog/Handlers/LoadBlogCategoryFeaturedArticle";
import BlogCategoryFeaturedArticleComponent, {
    BlogCategoryFeaturedArticlePresentationProps,
} from "@store/Library/Components/Toolkit/Blog/BlogCategoryFeaturedArticle";
import { usePageLinkByNodeId } from "@toolkit/Framework/Store/Links/LinksHooks";
import { PageChildrenSelectorFieldValue } from "@toolkit/Framework/Types/FieldDefinition";
import * as React from "react";
import { FC, useEffect } from "react";

const enum fields {
    featuredArticle = "featuredArticle",
    featuredImage = "featuredImage",
    featuredText = "featuredText",
    readMoreLabel = "readMoreLabel",
    surtitle = "surtitle",
}

interface BlogCategoryFeaturedArticleProps extends WidgetProps {
    fields: {
        [fields.featuredArticle]: PageChildrenSelectorFieldValue;
        [fields.featuredImage]: string;
        [fields.featuredText]: string;
        [fields.readMoreLabel]: string;
        [fields.surtitle]: string;
    };
}

export interface BlogCategoryFeaturedArticleStyles {
    blogCategoryFeaturedArticleComponent?: BlogCategoryFeaturedArticlePresentationProps;
}

export const blogCategoryFeaturedArticleStyles: BlogCategoryFeaturedArticleStyles = {};

const styles = blogCategoryFeaturedArticleStyles;

const BlogCategoryFeaturedArticle: FC<BlogCategoryFeaturedArticleProps> = ({ fields }) => {
    const { featuredArticle, featuredImage, featuredText, readMoreLabel, surtitle } = fields;
    const dispatch = useAppDispatch();
    const pageLink = usePageLinkByNodeId(featuredArticle?.value);
    const blogFeaturedArticleDataView = useBlogCategoryFeaturedArticleDataView(pageLink?.url || "");
    const { isLoading, value: articles } = blogFeaturedArticleDataView;

    useEffect(() => {
        if (!isLoading && !articles && pageLink) {
            dispatch(
                loadBlogCategoryFeaturedArticle({
                    pageLink: pageLink.url,
                }),
            );
        }
    }, [pageLink]);

    if (!articles?.length) {
        return null;
    }
    const article = articles[0];
    const {
        id,
        fields: { publishDate, title },
    } = article;

    return (
        <BlogCategoryFeaturedArticleComponent
            key={id}
            {...styles.blogCategoryFeaturedArticleComponent}
            imagePath={featuredImage}
            linkHref={pageLink?.url}
            publishDate={publishDate}
            readMoreLabel={readMoreLabel}
            summary={featuredText}
            surtitle={surtitle}
            title={title}
        />
    );
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: BlogCategoryFeaturedArticle,
    definition: {
        group: constants.widgetGroup.blog,
        allowedContexts: [constants.pageType.blogCategoryPage],
        displayName: "Featured Article",
        fieldDefinitions: [
            {
                name: fields.featuredArticle,
                displayName: "Select a Featured Article",
                editorTemplate: "PageChildrenSelectorField",
                fieldType: "Contextual",
                isRequired: true,
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                showOnlyFirstLevelChildren: true,
            },
            {
                ...blogFeaturedImageField,
                name: fields.featuredImage,
                displayName: "Image",
                isRequired: true,
                tooltip:
                    "For best visual effect, we recommend an image with a 4:3 aspect ratio and a size of at least 696px x 522px.",
                tooltipPosition: "left",
            },
            {
                ...blogFeaturedTextField,
                name: fields.featuredText,
                displayName: "Text",
                isRequired: true,
                tooltip:
                    "For best visual effect, we recommend a text length of around 215 characters. If the text is too long, it will be cut off.",
                tooltipPosition: "left",
            },
            {
                name: fields.surtitle,
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Featured Story",
            },
            {
                name: fields.readMoreLabel,
                displayName: "Read More Link Label",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Read Story",
            },
        ],
    },
};

export default widgetModule;
